import { Box, Stack, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { MdKeyboardArrowRight } from 'react-icons/md';
import {
  amplitude,
  presignedURLForJDFileUpload,
  fileUploadToS3,
  parseJD,
  IJob,
  JobType,
  IJDParserDTO,
  IFileInfoDTO,
} from 'wisestep-api';
import {
  FileTypeVariants,
  FileUploadVariants,
  ScrollableTabs,
  useWsEditor,
  WsButton,
  WsColors,
  WsDialog,
  WsDialogSize,
  WsEditor,
  WsFileUpload,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../src/context';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore, INotification } from '../../../../utils/NotificationStore';
import { getJobIndustries, getCategories, IJobIndustry } from '../../helper/JobHelperService';

export interface SimpleDialogProps {
  open: boolean;
  onClose: (value: string) => void;
  onJDParsing: (job: IJob) => void;
}

const JDUploadOrPasteDialogue = (props: SimpleDialogProps) => {
  const { open, onClose, onJDParsing } = props;
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  //const [jobEntity, setJobEntity] = useState<IJob>({});
  const [tabPanelPage, setTabPanelPage] = useState(0);
  const [jdPastedText, setJDPastedText] = useState<string | null>('');
  const [selectedJDFile, setSelectedJDFile] = useState<File>();

  const jobIndustries = getJobIndustries();
  const jobCategories = getCategories();

  const onJobDescUpdate = (args: string | null) => setJDPastedText(args);

  const jdEditor = useWsEditor('', onJobDescUpdate);

  const replaceUnnecessaryTags = (text: string) => {
    if (!text) {
      return text;
    }

    return text
      .replace(/\u00a0/g, '')
      .replace(/\\&nbsp;/g, '')
      .replace(/\nbsp;/g, '')
      .replace(/\nbsp/g, '')
      .replace(/\\Nbsp/g, '');
  };

  const onJDParseSubmit = () => {
    if (tabPanelPage != 1) {
      amplitude('job_paste', {
        clickedOn: 'Paste JD',
        pageName: 'new jobs',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      });
    }
    amplitude('job_upload_success', {
      clickedOn: 'Parse JD',
      pageName: 'new jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    if (jdPastedText) {
      const parserDTO: IJDParserDTO = {
        mode: 'text',
        input: jdPastedText,
        output: 'json',
      };

      parseJDByParserDTO(parserDTO);
    } else if (selectedJDFile) {
      onJDFileSelection(selectedJDFile);
    } else {
      setNotification(failed('Job Description is empty.'));
    }
  };

  const onFileUpload = (files: File[]) => {
    //onJDFileSelection(files[0]);
    setSelectedJDFile(files[0]);
    setNotification(success('File Uploaded '));
  };

  const onFileRejected = (files: FileRejection[]) => {
    amplitude('job_upload_failed', {
      clickedOn: 'Parse JD',
      pageName: 'new jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    if (files) {
      //do nothing
    }
    setNotification(failed('Invalid file selected.'));
  };

  const onJDFileSelection = async (file: File) => {
    if (!file) {
      return;
    }
    const request: IFileInfoDTO = {
      fileName: file.name,
      fileType: file.type,
      applyMaxExpiry: true,
    };

    const result = await presignedURLForJDFileUpload(request);
    const uploadResult = await fileUploadToS3(file, result.data.preSignedUrl);
    if (uploadResult) {
      const parserDTO: IJDParserDTO = {
        mode: 'file',
        input: result.data.preSignedUrl.split('?')[0],
        output: 'json',
      };

      parseJDByParserDTO(parserDTO);
    }
  };

  const parseJDByParserDTO = async (parserDTO: IJDParserDTO) => {
    const parseResult = await parseJD(parserDTO);
    if (parseResult) {
      const job: IJob = {};
      const skills = parseResult.data.parsed_jd.skills ? parseResult.data.parsed_jd.skills : null;
      job.skills = skills;
      job.title = replaceUnnecessaryTags(parseResult.data.parsed_jd.jobTitles);
      job.workAuthorization = parseResult.data.parsed_jd.workAuthorization
        ? parseResult.data.parsed_jd.workAuthorization.join(',')
        : null;
      if (parseResult.data.parsed_jd.compensations) {
        job.salaryCurrency = parseResult.data.parsed_jd.compensations[0].currencies
          ? parseResult.data.parsed_jd.compensations[0].currencies[0]
          : null;
        if (parseResult.data.parsed_jd.compensations[0].ranges) {
          job.minimumSalary = parseResult.data.parsed_jd.compensations[0].ranges.min;
          job.maximumSalary = parseResult.data.parsed_jd.compensations[0].ranges.max;
        }
      }

      job.companyName = parseResult?.data.parsed_jd?.clientName[0] ?? null;
      job.locations = parseResult.data.parsed_jd.locations
        ? parseResult.data.parsed_jd.locations
        : null;
      job.clientRecruiterName = parseResult.data.parsed_jd.hiringManager
        ? parseResult.data.parsed_jd.hiringManager[0]
        : null;
      job.description = parseResult.data.parsed_jd.jobDescription
        ? parseResult.data.parsed_jd.jobDescription.join(' <br> ')
        : null;
      job.description = replaceUnnecessaryTags(job.description ? job.description : '');

      if (parseResult.data.parsed_jd.jobIndustry) {
        const jobIndustryName = parseResult.data.parsed_jd.jobIndustry;
        const matched: IJobIndustry[] = jobIndustries.filter((obj) => obj.label == jobIndustryName);
        if (!isEmpty(matched)) {
          job.jobIndustryName = matched[0].value;
        }
      }

      if (parseResult.data.parsed_jd.jobCategory) {
        const matched: IJobIndustry[] = jobCategories.filter(
          (obj) => obj.label == parseResult.data.parsed_jd.jobCategory
        );
        if (!isEmpty(matched)) {
          job.jobCategory = JSON.stringify(matched[0].value);
          //jobOccupations = jobCategories[k].occupations;
        }
      }

      job.visaRequirements = parseResult.data.parsed_jd.visa
        ? parseResult.data.parsed_jd.visa.join(', ')
        : null;
      if (parseResult.data.parsed_jd.jobType) {
        switch (parseResult.data.parsed_jd.jobType.toLowerCase()) {
          case 'contract':
            job.jobType = JobType.Contract;
            break;
          case 'permanent':
            job.jobType = JobType.Permanent;
            break;
          case 'temporary':
            job.jobType = JobType.Temporary;
            break;
          case 'parttime':
            job.jobType = JobType.PartTime;
            break;
          case 'fulltime':
            job.jobType = JobType.Permanent;
            break;
          case 'internship':
            job.jobType = JobType.Internship;
            break;
          case 'contracttohirew2':
            job.jobType = JobType.ContractToHireW2;
            break;
          case 'contractw2':
            job.jobType = JobType.ContractW2;
            break;
          case 'contractindependent':
            job.jobType = JobType.ContractIndependent;
            break;
          case 'contracttohireindependent':
            job.jobType = JobType.ContractToHireIndependent;
            break;
          case 'contractcorptocorp':
            job.jobType = JobType.ContractCorpToCorp;
            break;
          case 'permanentandcontract':
            job.jobType = JobType.PermanentAndContract;
            break;
          case 'freelance':
            job.jobType = JobType.Freelance;
            break;
          case 'thirdparty':
            job.jobType = JobType.ThirdParty;
            break;
          default:
            break;
        }
      }
      onJDParsing(job);
    } else {
      const notifyObj: INotification = { message: 'JD parser sent empty response.', type: 'error' };
      setNotification(notifyObj);
    }
  };

  return (
    <WsDialog aria-labelledby="Jd upload" size={WsDialogSize.LARGE} openDialog={open}>
      <Box p={1} bgcolor={WsColors.TertiaryShades50}>
        <Grid item container xs={12}>
          <Grid item container xs={6} justifyContent="flex-start">
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Parse from Job Description
            </WsTypography>
          </Grid>
          <Grid item container xs={6} justifyContent="flex-end">
            <WsButton onClick={() => onClose('choice')} variant="text" size="large">
              X
            </WsButton>
          </Grid>
        </Grid>
      </Box>
      <Box>
        <ScrollableTabs
          scrollList={[{ tabName: 'Enter JD' }, { tabName: 'Upload a File' }]}
          getTabIndex={(index: number) => {
            index === 1
              ? amplitude('Job_Upload"', {
                  clickedOn: 'upload',
                  pageName: 'new jobs',
                  pageUrl: window.location.href,
                  workspaceName: workspace.name,
                  planType: workspace.planType,
                  interfaceType: 'Web',
                })
              : amplitude('job_Parse"', {
                  clickedOn: 'parse',
                  pageName: 'new jobs',
                  pageUrl: window.location.href,
                  workspaceName: workspace.name,
                  planType: workspace.planType,
                  interfaceType: 'Web',
                });
            console.log(index);
            setTabPanelPage(index);
          }}
          defaultValue={0}
        />
        <Stack m={4} p={2}>
          {tabPanelPage === 0 && (
            <div
              style={{
                border: `1.5px solid ${WsColors.BorderGrey}`,
                borderRadius: 4,
              }}
            >
              <WsEditor editor={jdEditor} resizable={true} editorHeight={172} />
            </div>
          )}
          {tabPanelPage === 1 && (
            <WsFileUpload
              multiple={false}
              name="JDFileUploadToParse"
              variant={FileUploadVariants.APP}
              onUpload={onFileUpload}
              placeholderText="Upload JD"
              placeholderHelperText={selectedJDFile?.name}
              maxFileSize={10}
              onFileRejected={onFileRejected}
              fileTypes={FileTypeVariants.RESUME}
            />
          )}
        </Stack>
      </Box>

      <Grid item xs={12} p={2} container justifyContent="flex-end" position="static">
        <WsButton variant="contained" endIcon={<MdKeyboardArrowRight />} onClick={onJDParseSubmit}>
          Parse from JD
        </WsButton>
      </Grid>
    </WsDialog>
  );
};

export default JDUploadOrPasteDialogue;
